import Prism from 'prismjs';
import 'prismjs/plugins/keep-markup/prism-keep-markup';

import { GlossaryData } from 'src/features/glossary/glossary.types';
import targetMachineImage from 'src/images/pngs/tasks/target-machine.png';
import virtualEnvImage from 'src/images/pngs/tasks/virtual-env.png';

import { ExperimentSupportedRoomV1 } from '../hooks/use-combine-start-machine-attackbox-widget-experiment';

const escapeRegex = (str: string): string => str.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&');

export const wrapGlossaryTermInSpan = (term: string, content: string): string => {
  const safeTerm = escapeRegex(term);

  const glossaryTermReplaceRegex = new RegExp(
    `(<code[^>]*?>.*?\\b${safeTerm}\\b.*?</code>)|(?<!<[^>]*)(\\b${safeTerm}\\b)(?![^<]*>)`,
    'gi',
  );

  return content.replace(glossaryTermReplaceRegex, (match, codeBlock, glossaryTerm) => {
    if (codeBlock) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return codeBlock;
    }
    if (glossaryTerm) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      return `<span data-testid="glossary-term" class="glossary-term">${glossaryTerm}</span>`;
    }
    return match;
  });
};

export const applyPrismStyling = (content: string): string => {
  const prismContainer = document.createElement('div');
  prismContainer.innerHTML = content;
  Prism.highlightAllUnder(prismContainer);
  const htmlString = prismContainer.innerHTML;
  prismContainer.remove();
  return htmlString;
};

const replaceString = (replaceableText: string, replaceRegexTextMatch: string, value?: string) => {
  if (value) {
    const machineIPReplaceRegex = new RegExp(`\\b${replaceRegexTextMatch}\\b`, 'g');
    return replaceableText.replace(machineIPReplaceRegex, value);
  }
  return replaceableText;
};

export const replaceContent = (
  content: string,
  glossaryData: GlossaryData,
  machineIP?: string,
  attackBoxPrivateIP?: string,
  shouldSeeVmStart = false,
  currentAzureLabId?: string,
) => {
  let refiningContent = content;

  if (shouldSeeVmStart) {
    refiningContent = refiningContent.replace(
      'Click the "Start Machine" button. Once loaded in Split View in your browser, you will have access to a machine you\'ll use to hack a fake bank application called FakeBank. If you don\'t see the machine appear, use the blue Show Split View button on the top-right of this page.',
      "When you first open this task, your screen will split and load a machine you'll use to hack a fake bank application called FakeBank. You can also click on 'Start Machine' to deploy it anytime. If your screen doesn’t split, use the blue Show Split View button on the top-left of this page.",
    );
  }

  refiningContent = replaceString(refiningContent, 'MACHINE_IP', machineIP);
  refiningContent = replaceString(refiningContent, 'LAB_WEB_URL', machineIP?.replace(/\./g, '-'));
  refiningContent = replaceString(refiningContent, 'CONNECTION_IP', attackBoxPrivateIP);
  refiningContent = replaceString(refiningContent, 'AZURE_LAB_ID', currentAzureLabId);

  for (const glossary of glossaryData) {
    const { term } = glossary;
    refiningContent = wrapGlossaryTermInSpan(term, refiningContent);
  }

  refiningContent = applyPrismStyling(refiningContent);

  return refiningContent;
};

export const replaceContentForBreakdownExperiment = ({
  roomCode,
  content,
  taskTitle,
  taskNo,
}: {
  roomCode: string;
  content: string;
  taskTitle?: string;
  taskNo?: number;
}) => {
  if (roomCode !== 'offensivesecurityintro') {
    return content;
  }

  // Use DOMParser for safer HTML parsing
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');

  // Task 1 modifications
  if (taskNo === 1 && taskTitle) {
    const titleEl = document.createElement('h3');
    titleEl.textContent = taskTitle;
    if (doc.body.childNodes.length > 1) {
      doc.body.insertBefore(titleEl, doc.body.childNodes[1] as Node);
    }
    if (doc.body.childNodes.length > 7) {
      const replacementElement = document.createElement('h3');
      replacementElement.textContent = (doc.body.childNodes[7] as Node).textContent;
      doc.body.replaceChild(replacementElement, doc.body.childNodes[7] as Node);
    }

    // Remove specific nodes if they exist
    const nodesToRemove = [];
    if (doc.body.childNodes.length > 11) nodesToRemove.push(doc.body.childNodes[11]);
    for (const node of nodesToRemove) {
      node?.parentNode?.removeChild(node);
    }
  }

  // Task 2 modifications
  if (taskNo === 2) {
    if (taskTitle) {
      const titleEl = document.createElement('h3');
      titleEl.textContent = taskTitle;
      titleEl.style.marginTop = '0';

      if (doc.body.childNodes.length > 0) {
        doc.body.insertBefore(titleEl, doc.body.childNodes[0] as Node);
      }
    }

    // Remove specific nodes if they exist
    const nodesToRemove = [];
    if (doc.body.childNodes.length > 10) nodesToRemove.push(doc.body.childNodes[10]);
    if (doc.body.childNodes.length > 12) nodesToRemove.push(doc.body.childNodes[12]);

    for (const node of nodesToRemove) {
      node?.parentNode?.removeChild(node);
    }

    // Add specific paragraphs
    const paragraphTexts = [
      "If you were a penetration tester or security consultant, this is an exercise you'd perform for companies to test for vulnerabilities in their web applications and find hidden pages to investigate for vulnerabilities.",
      'Terminate the machine by clicking the red "Terminate" button at the top of the page or by clicking on the terminate button on the split screen.',
    ];

    for (const text of paragraphTexts) {
      const p = document.createElement('p');
      p.textContent = text;
      doc.body.append(p);
    }
  }

  // Task 3 modifications
  if (taskNo === 3) {
    if (doc.body.childNodes.length > 0) {
      const replacementElement = document.createElement('h3');
      replacementElement.textContent = (doc.body.childNodes[0] as Node).textContent;
      replacementElement.style.marginTop = '0';
      doc.body.replaceChild(replacementElement, doc.body.childNodes[0] as Node);
    }

    if (doc.body.childNodes.length > 8) {
      const replacementElement = document.createElement('h3');
      replacementElement.textContent = (doc.body.childNodes[8] as Node).textContent;
      doc.body.replaceChild(replacementElement, doc.body.childNodes[8] as Node);
    }
  }

  return doc.body.innerHTML;
};

export const replaceContentForCombineAttackboxExperiment = (roomCode: string, content: string) => {
  switch (roomCode as ExperimentSupportedRoomV1) {
    case ExperimentSupportedRoomV1.LINUX_FUNDAMENTALS_PART2: {
      return content
        .replace('https://assets.tryhackme.com/additional/linux-fundamentals/part2/deploy1.png', targetMachineImage)
        .replace('https://assets.tryhackme.com/additional/linux-fundamentals/part2/ab1.png', virtualEnvImage)
        .replace('Looking at the top of the page', 'Looking at the top of the task');
    }
    case ExperimentSupportedRoomV1.LINUX_FUNDAMENTALS_PART3: {
      return content
        .replace('https://assets.tryhackme.com/additional/linux-fundamentals/part3/deploy1.png', targetMachineImage)
        .replace('https://assets.tryhackme.com/additional/linux-fundamentals/part3/deploy2.png', virtualEnvImage)
        .replace('Looking at the top of the page', 'Looking at the top of the task');
    }
    case ExperimentSupportedRoomV1.VULNIVERSITY:
    case ExperimentSupportedRoomV1.ROOT_ME: {
      return '';
    }
    default: {
      return content;
    }
  }
};
